import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import axios from "axios";
import "../../css/ProductPage.css";
import { useAuth } from "../../context/auth";
import { toast } from "react-hot-toast";
import Loader from "../../components/Loader";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NewReleasesIcon from "@mui/icons-material/NewReleases";

const ProductPage = () => {
  const [subCategory, setSubCategory] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [products, setProducts] = useState([]);
  const { category, subcategory } = useParams();
  const [wishlist, setWishlist] = useState([]);
  const [auth] = useAuth();
  const [priceRange, setPriceRange] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const apiUrl = `${process.env.REACT_APP_API}`;

  // Scroll position management
  useEffect(() => {
    // Only restore scroll after everything is loaded
    if (products.length > 0 && !loading) {
      const timer = setTimeout(() => {
        const savedScroll = sessionStorage.getItem('productPageScroll');
        if (savedScroll) {
          window.scrollTo(0, parseInt(savedScroll));
          sessionStorage.removeItem('productPageScroll');
        }
      }, 300); // Wait 300ms for images to load

      return () => clearTimeout(timer);
    }
  }, [products.length, loading]);




  const handlePriceRangeChange = (event) => {
    setPriceRange(event.target.value);
  };

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const response = await axios.get(`${apiUrl}/categories/${category}/subcategories`);
        setSubCategory(response.data.subCategories);
      } catch (error) {
        console.error(error);
      }
    };

    fetchSubcategories();
    setSelectedSubCategory(subcategory);
  }, [subcategory, category]);

  useEffect(() => {
    if (selectedSubCategory) {
      setLoading(true);
      axios
          .get(`${apiUrl}/categories/${category}/subcategory/${selectedSubCategory}/products`)
          .then((response) => {
            const filteredProducts = filterProductsByPrice(response.data);
            const filteredProductsQ = filterProductByQuantity(filteredProducts);
            setProducts(filteredProductsQ);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
    }
  }, [selectedSubCategory, priceRange, category]);

  const filterProductByQuantity = (products) => {
    return products.filter((p) => {
      return p.productSize.sizes.some((size) => size.quantity > 0);
    });
  };

  const filterProductsByPrice = (products) => {
    if (priceRange === "LessThan50") {
      return products.filter((p) =>
          p.salePrice > 0 ? p.salePrice < 50 : p.productPrice < 50
      );
    } else if (priceRange === "50to100") {
      return products.filter((p) =>
          p.salePrice > 0
              ? p.salePrice >= 50 && p.salePrice <= 100
              : p.productPrice >= 50 && p.productPrice <= 100
      );
    } else if (priceRange === "MoreThan100") {
      return products.filter((p) =>
          p.salePrice > 0 ? p.salePrice > 100 : p.productPrice > 100
      );
    } else {
      return products;
    }
  };

  const fetchWishlist = async () => {
    try {
      if (auth && auth.currentUser && auth.currentUser.id) {
        const response = await axios.get(`${apiUrl}/wishlist`);
        setWishlist(response.data.products);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (auth && auth.currentUser && auth.currentUser.id) {
      fetchWishlist();
    }
  }, [auth]);

  const isInWishlist = (productId) => {
    return wishlist.some((item) => item._id === productId);
  };

  const addToWishlist = async (productId) => {
    try {
      await axios.post(`${apiUrl}/wishlist/add`, { productId });
      toast.success("Added to Wishlist");
      await fetchWishlist();
    } catch (error) {
      console.error(error);
      toast.error("Error adding to wishlist");
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      if (!auth) {
        return;
      }
      await axios.delete(`${apiUrl}/wishlist/delete/${productId}`);
      toast.success("Removed from Wishlist");
      await fetchWishlist();
    } catch (err) {
      console.error(err.message);
      toast.error("Error removing from wishlist");
    }
  };

  const getDays = (product) => {
    const productDate = product.createdAt.substring(0, 10);
    const createdDate = new Date(productDate);
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    const todayDate = new Date(formattedDate);
    const totalDays = todayDate - createdDate;
    const daysDifference = Math.floor(totalDays / (1000 * 60 * 60 * 24));
    return daysDifference;
  };

  const formatCategoryName = (slug) => {
    return slug
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
  };

  const handleProductClick = () => {
    // Save current scroll position before navigating
    sessionStorage.setItem(`scrollPosition-${category}-${subcategory}`, window.scrollY.toString());
  };
  return (
      <>
        <Header />
        {loading && <Loader />}

        <div className="product-page">
          <div className="container py-4">
            {/* Page Header */}
            <div className="product-page-header mb-4">
              <h1 className="product-page-title">
                {formatCategoryName(selectedSubCategory || subcategory)}
              </h1>
              <p className="product-page-subtitle">
                {products.length} {products.length === 1 ? 'Product' : 'Products'} Available
              </p>
            </div>

            {/* Filters */}
            <div className="filters-section mb-4">
              <div className="row g-3">
                <div className="col-md-3 col-6">
                  <label className="filter-label">
                    <FilterListIcon fontSize="small" className="me-1" />
                    Category
                  </label>
                  <select
                      className="form-select filter-select"
                      value={selectedSubCategory}
                      onChange={(event) => setSelectedSubCategory(event.target.value)}
                  >
                    {subCategory.map((data) => (
                        <option key={data.name} value={data.slug}>
                          {data.name}
                        </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3 col-6">
                  <label className="filter-label">
                    <LocalOfferIcon fontSize="small" className="me-1" />
                    Price Range
                  </label>
                  <select
                      className="form-select filter-select"
                      value={priceRange}
                      onChange={handlePriceRangeChange}
                  >
                    <option value="">All Prices</option>
                    <option value="LessThan50">Under $50</option>
                    <option value="50to100">$50 - $100</option>
                    <option value="MoreThan100">$100+</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {!loading && products.length > 0 && (
                <div className="products-grid">
                  {products.map((product) => (
                      <div key={product._id} className="product-card-wrapper">
                        <div className="product-card">
                          {/* Product Image */}
                          <div className="product-image-container">
                            <Link
                                to={`/products/${product._id}`}
                                onClick={() => sessionStorage.setItem('productPageScroll', window.scrollY)}
                            >
                              <img
                                  src={product.images.front}
                                  alt={product.productName}
                                  className="product-image"
                                  loading="lazy"
                              />
                            </Link>

                            {/* New Badge */}
                            {getDays(product) <= 90 && (
                                <div className="new-badge">
                                  <NewReleasesIcon fontSize="small" className="me-1" />
                                  NEW
                                </div>
                            )}

                            {/* Sale Badge */}
                            {product.salePrice > 0 && (
                                <div className="sale-badge">
                                  <LocalOfferIcon fontSize="small" className="me-1" />
                                  {Math.round(
                                      ((product.productPrice - product.salePrice) /
                                          product.productPrice) *
                                      100
                                  )}
                                  % OFF
                                </div>
                            )}

                            {/* Wishlist Button */}
                            <button
                                className={`wishlist-btn ${
                                    isInWishlist(product._id) ? "active" : ""
                                }`}
                                onClick={() => {
                                  if (!localStorage.getItem("auth")) {
                                    toast.error("Please log in to add to wishlist");
                                    return;
                                  }
                                  if (isInWishlist(product._id)) {
                                    removeFromWishlist(product._id);
                                  } else {
                                    addToWishlist(product._id);
                                  }
                                }}
                            >
                              <FavoriteIcon
                                  style={{
                                    fontSize: "1.3rem",
                                    color: isInWishlist(product._id) ? "#c70044" : "white"
                                  }}
                              />
                            </button>
                          </div>

                          {/* Product Info */}
                          <div className="product-info">
                            <Link
                                to={`/products/${product._id}`}
                                className="product-link"
                                onClick={handleProductClick}
                            >
                              <h5 className="product-name">{product.productName}</h5>
                            </Link>

                            <div className="product-pricing">
                              {product.salePrice > 0 ? (
                                  <>
                                    <span className="sale-price">${product.salePrice}</span>
                                    <span className="original-price">${product.productPrice}</span>
                                  </>
                              ) : (
                                  <span className="current-price">${product.productPrice}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
            )}

            {/* Empty State */}
            {!loading && products.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-card">
                    <LocalOfferIcon style={{ fontSize: "5rem", color: "#ccc" }} />
                    <h4>No Products Found</h4>
                    <p className="text-muted">
                      Exciting new products are on their way! Check back soon.
                    </p>
                  </div>
                </div>
            )}
          </div>
        </div>
        <Footer />
      </>
  );
};

export default ProductPage;